import { useCallback, useState, type FormEvent, type ReactNode } from "react";
import { ApiError, refusedAction } from "../api/client.js";
import type {
  BusCompressorDrive,
  BusConsumerCode,
  BusEligibilityRequest,
  BusEligibilityResponse,
  BusGasConnection,
  BusJurisdiction,
  BusPropertyUse,
  BusSystemConfiguration,
  BusTechnology,
  BusUnitInput,
} from "../api/types.js";
import { usePortal } from "../app/context.js";
import { href } from "../app/router.js";
import { humanise } from "../lib/format.js";

/**
 * The Boiler Upgrade Scheme grant screen.
 *
 * A verdict-input form, the assembled verdict, and — if eligible — the two BUS
 * clocks (application deadline and voucher expiry). No result is cached client
 * side: the input body has no persisted home, so a reload starts from the
 * same defaults again on purpose (see apps/api/src/http/bus-routes.ts's
 * header for why the eligibility endpoint is a POST).
 *
 * EVERY BUS FIGURE is provisional per handoff §09 until MCS/MID login unlocks
 * verification (~23 Sep 2026). The banner below says so at the top of the
 * screen, not in fine print at the bottom, because a customer being told they
 * qualify for £7,500 on the strength of a number nobody has confirmed is
 * exactly the failure mode the whole provenance system exists to prevent.
 * The `verdict.stamp.provenance` field is echoed back on every rendered
 * verdict, so the compliance-mode contract holds end to end even when a
 * portal user forgets what a provenance stamp is.
 */

const TECHNOLOGIES: readonly BusTechnology[] = [
  "air-to-water-heat-pump",
  "ground-source-heat-pump",
  "water-source-heat-pump",
  "air-to-air-heat-pump",
  "biomass-boiler",
];

const CONFIGURATIONS: readonly BusSystemConfiguration[] = [
  "sole-heat-source",
  "part-of-hybrid",
  "backup-only",
];

const COMPRESSOR_DRIVES: readonly BusCompressorDrive[] = [
  "electric",
  "gas-engine",
];

const PROPERTY_USES: readonly BusPropertyUse[] = [
  "residential",
  "social-housing",
];

const GAS_CONNECTIONS: readonly BusGasConnection[] = [
  "on-gas-grid",
  "off-gas-grid",
];

const JURISDICTIONS: readonly BusJurisdiction[] = [
  "england",
  "wales",
  "scotland",
  "northern-ireland",
];

const CONSUMER_CODES: readonly BusConsumerCode[] = [
  "hies",
  "renewable-energy-consumer-code",
  "chartered-trading-standards-institute-approved",
];

interface FormState {
  technology: BusTechnology;
  configuration: BusSystemConfiguration;
  scop: string;
  scopSource: string;
  compressorDrive: BusCompressorDrive;
  meetsFullDemand: boolean;
  units: BusUnitInput[];
  propertyUse: BusPropertyUse;
  gasConnection: BusGasConnection;
  rural: boolean;
  jurisdiction: BusJurisdiction | "";
  consumerCode: BusConsumerCode | "";
  onRedevelopedMcsScheme: boolean;
}

/**
 * Sensible starting point for a typical residential air-to-water install.
 * NEVER the answer — every field is editable and the compliance verdict
 * depends on what the user actually types — but the modal shape of an MCS
 * heat pump install today is a single unit, an average SCOP figure, and a
 * property on the gas grid, so defaulting to that makes the first
 * interaction one field to change (SCOP) rather than ten.
 */
const INITIAL_STATE: FormState = {
  technology: "air-to-water-heat-pump",
  configuration: "sole-heat-source",
  scop: "3.2",
  scopSource: "MCS 026 calculation",
  compressorDrive: "electric",
  meetsFullDemand: true,
  units: [{ id: "unit-1", capacityKwth: 8 }],
  propertyUse: "residential",
  gasConnection: "on-gas-grid",
  rural: false,
  jurisdiction: "",
  consumerCode: "",
  onRedevelopedMcsScheme: false,
};

function toRequest(state: FormState): BusEligibilityRequest | null {
  const scop = Number(state.scop);
  if (!Number.isFinite(scop) || scop <= 0) return null;
  if (state.units.length === 0) return null;
  const units = state.units.map((u, i) => {
    const capacityKwth = Number(u.capacityKwth);
    return {
      id: u.id || `unit-${i + 1}`,
      capacityKwth: Number.isFinite(capacityKwth) ? capacityKwth : 0,
    };
  });
  return {
    property: {
      use: state.propertyUse,
      gasConnection: state.gasConnection,
      rural: state.rural,
      ...(state.jurisdiction !== "" ? { jurisdiction: state.jurisdiction } : {}),
    },
    system: {
      technology: state.technology,
      configuration: state.configuration,
      units,
      scop,
      scopSource: state.scopSource,
      compressorDrive: state.compressorDrive,
      meetsFullDemand: state.meetsFullDemand,
    },
    ...(state.consumerCode !== "" || state.onRedevelopedMcsScheme
      ? {
          installer: {
            ...(state.consumerCode !== ""
              ? { consumerCode: state.consumerCode }
              : {}),
            ...(state.onRedevelopedMcsScheme
              ? { onRedevelopedMcsScheme: true }
              : {}),
          },
        }
      : {}),
  };
}

export function BusGrant({ jobId }: { jobId: string }): ReactNode {
  const { client } = usePortal();
  const [state, setState] = useState<FormState>(INITIAL_STATE);
  const [result, setResult] = useState<BusEligibilityResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const patch = useCallback(<K extends keyof FormState>(k: K, v: FormState[K]) => {
    setState((prev) => ({ ...prev, [k]: v }));
  }, []);

  const addUnit = useCallback(() => {
    setState((prev) => ({
      ...prev,
      units: [
        ...prev.units,
        { id: `unit-${prev.units.length + 1}`, capacityKwth: 0 },
      ],
    }));
  }, []);

  const removeUnit = useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      // A submission needs at least one unit — hide the last remove button
      // in the markup rather than rely on this to no-op silently.
      units: prev.units.filter((_, i) => i !== index),
    }));
  }, []);

  const setUnit = useCallback(
    (index: number, field: "id" | "capacityKwth", value: string) => {
      setState((prev) => ({
        ...prev,
        units: prev.units.map((u, i) =>
          i !== index
            ? u
            : field === "id"
              ? { ...u, id: value }
              : { ...u, capacityKwth: Number(value) || 0 },
        ),
      }));
    },
    [],
  );

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const request = toRequest(state);
      if (!request) {
        setError(
          "SCOP must be a positive number and at least one heat-pump unit " +
            "with a real capacity is required.",
        );
        return;
      }
      setError(null);
      setPending(true);
      try {
        const response = await client.busEligibility(jobId, request);
        setResult(response);
      } catch (caught) {
        setResult(null);
        if (caught instanceof ApiError) {
          const refused = refusedAction(caught);
          if (caught.code === "entitlement_required") {
            setError(
              "This organisation is not entitled to the heat-pump-design " +
                "module, which the BUS route sits behind. Contact your " +
                "account manager to enable it.",
            );
          } else if (refused) {
            setError(
              `${caught.message} (${refused.resource} : ${refused.action})`,
            );
          } else {
            setError(caught.message);
          }
        } else {
          setError(
            "The BUS eligibility request could not be sent. Check your " +
              "connection and try again.",
          );
        }
      } finally {
        setPending(false);
      }
    },
    [client, jobId, state],
  );

  return (
    <>
      <div className="page-header">
        <h1>Boiler Upgrade Scheme — grant check</h1>
        <p className="lede">
          Assess this job against the Ofgem BUS ruleset. Nothing is persisted:
          the check runs on what you type, and the result is a verdict, not a
          voucher application.
        </p>
        <p>
          <a href={href.job(jobId)}>Back to the job</a>
        </p>
      </div>

      <div
        className="callout warn"
        role="note"
        aria-label="Provisional data warning"
      >
        <strong>Provisional.</strong> Every BUS figure this platform uses is
        marked <code>provenance: provisional</code> in the ruleset stamp until
        cross-validation against the MCS Heat Load Calculator is complete
        (handoff §9 — the £7,500 / £2,500 / £5,000 amounts, the £9,000 off-gas
        uplift, the 45/70 kWth ceilings, the 2.8 SCOP minimum, the voucher
        validity and redemption windows, and the 2030 closure date all rest on
        the same read of Ofgem's Installer Guidance and have not yet been
        checked against the operator's own calculator). Do not present a grant
        amount to a customer on the strength of this verdict alone.
      </div>

      <form onSubmit={onSubmit} className="stack" aria-labelledby="bus-form-heading">
        <h2 id="bus-form-heading">System and property</h2>

        <fieldset className="stack">
          <legend>Heat pump system</legend>

          <label className="field">
            <span>Technology</span>
            <select
              value={state.technology}
              onChange={(e) => patch("technology", e.target.value as BusTechnology)}
            >
              {TECHNOLOGIES.map((t) => (
                <option key={t} value={t}>
                  {humanise(t)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Configuration</span>
            <select
              value={state.configuration}
              onChange={(e) =>
                patch("configuration", e.target.value as BusSystemConfiguration)
              }
            >
              {CONFIGURATIONS.map((c) => (
                <option key={c} value={c}>
                  {humanise(c)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>SCOP</span>
            <input
              type="number"
              step="0.01"
              value={state.scop}
              onChange={(e) => patch("scop", e.target.value)}
              // No `min`/`max`/`required` HTML5 attributes: the "SCOP must be
              // positive" refusal is a business rule and lives in `toRequest`
              // so the fallible-error test can drive it. HTML5 form validation
              // would silently block submit BEFORE our own guard ran, which
              // would (a) prevent the message the screen was written to show,
              // and (b) leave the fallible test unable to distinguish "the
              // guard fires" from "the browser blocked it".
            />
          </label>

          <label className="field">
            <span>SCOP source</span>
            <input
              type="text"
              value={state.scopSource}
              onChange={(e) => patch("scopSource", e.target.value)}
              required
            />
          </label>

          <label className="field">
            <span>Compressor drive</span>
            <select
              value={state.compressorDrive}
              onChange={(e) =>
                patch("compressorDrive", e.target.value as BusCompressorDrive)
              }
            >
              {COMPRESSOR_DRIVES.map((d) => (
                <option key={d} value={d}>
                  {humanise(d)}
                </option>
              ))}
            </select>
          </label>

          <label className="field checkbox">
            <input
              type="checkbox"
              checked={state.meetsFullDemand}
              onChange={(e) => patch("meetsFullDemand", e.target.checked)}
            />
            <span>Meets the property's full space-heating demand</span>
          </label>
        </fieldset>

        <fieldset className="stack">
          <legend>Units</legend>
          <p className="muted small">
            One entry per outdoor unit; a monobloc install has exactly one.
          </p>
          {state.units.map((unit, i) => (
            <div key={i} className="row">
              <label className="field">
                <span>Reference</span>
                <input
                  type="text"
                  value={unit.id}
                  onChange={(e) => setUnit(i, "id", e.target.value)}
                />
              </label>
              <label className="field">
                <span>Capacity (kWth)</span>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={unit.capacityKwth}
                  onChange={(e) => setUnit(i, "capacityKwth", e.target.value)}
                />
              </label>
              {state.units.length > 1 ? (
                <button
                  type="button"
                  className="link"
                  onClick={() => removeUnit(i)}
                  aria-label={`Remove unit ${i + 1}`}
                >
                  Remove
                </button>
              ) : null}
            </div>
          ))}
          <button type="button" className="link" onClick={addUnit}>
            Add another unit
          </button>
        </fieldset>

        <fieldset className="stack">
          <legend>Property</legend>

          <label className="field">
            <span>Use</span>
            <select
              value={state.propertyUse}
              onChange={(e) => patch("propertyUse", e.target.value as BusPropertyUse)}
            >
              {PROPERTY_USES.map((u) => (
                <option key={u} value={u}>
                  {humanise(u)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Gas connection</span>
            <select
              value={state.gasConnection}
              onChange={(e) =>
                patch("gasConnection", e.target.value as BusGasConnection)
              }
            >
              {GAS_CONNECTIONS.map((g) => (
                <option key={g} value={g}>
                  {humanise(g)}
                </option>
              ))}
            </select>
          </label>

          <label className="field checkbox">
            <input
              type="checkbox"
              checked={state.rural}
              onChange={(e) => patch("rural", e.target.checked)}
            />
            <span>Rural (affects one warning)</span>
          </label>

          <label className="field">
            <span>Jurisdiction override</span>
            <select
              value={state.jurisdiction}
              onChange={(e) =>
                patch("jurisdiction", e.target.value as BusJurisdiction | "")
              }
            >
              <option value="">Read from the job's survey</option>
              {JURISDICTIONS.map((j) => (
                <option key={j} value={j}>
                  {humanise(j)}
                </option>
              ))}
            </select>
          </label>
        </fieldset>

        <fieldset className="stack">
          <legend>Installer (optional)</legend>

          <label className="field">
            <span>Consumer code</span>
            <select
              value={state.consumerCode}
              onChange={(e) =>
                patch("consumerCode", e.target.value as BusConsumerCode | "")
              }
            >
              <option value="">Not stated</option>
              {CONSUMER_CODES.map((c) => (
                <option key={c} value={c}>
                  {humanise(c)}
                </option>
              ))}
            </select>
          </label>

          <label className="field checkbox">
            <input
              type="checkbox"
              checked={state.onRedevelopedMcsScheme}
              onChange={(e) => patch("onRedevelopedMcsScheme", e.target.checked)}
            />
            <span>On the redeveloped MCS scheme (post-2025 changes)</span>
          </label>
        </fieldset>

        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" className="primary" disabled={pending}>
          {pending ? "Assessing…" : "Assess eligibility"}
        </button>
      </form>

      {result ? <VerdictPanel response={result} /> : null}
    </>
  );
}

function VerdictPanel({ response }: { response: BusEligibilityResponse }): ReactNode {
  const { verdict, assembledFrom } = response;
  const grantLine = verdict.grant
    ? `£${verdict.grant.amountGbp.toLocaleString("en-GB")}${
        verdict.grant.upliftApplied ? " — off-gas-grid uplift applied" : ""
      }`
    : "no grant";

  return (
    <section aria-labelledby="verdict-heading" className="stack">
      <h2 id="verdict-heading">Verdict</h2>

      <p
        className={verdict.eligible ? "verdict eligible" : "verdict ineligible"}
        role="status"
      >
        <strong>{verdict.eligible ? "Eligible" : "Not eligible"}</strong> —{" "}
        {grantLine}
      </p>

      {verdict.grant ? (
        <p className="muted small">
          Basis: {verdict.grant.basis}
        </p>
      ) : null}

      <p className="muted small">
        Assessed on {verdict.stamp.assessedOn} against ruleset{" "}
        <code>{verdict.stamp.rulesetId}@{verdict.stamp.rulesetVersion}</code>,
        provenance <code>{verdict.stamp.provenance}</code>. Rules bundle{" "}
        <code>{verdict.stamp.rulesBundleVersion}</code>.
      </p>

      {verdict.stamp.unverified && verdict.stamp.unverified.length > 0 ? (
        <div className="callout warn">
          <strong>Unverified fields:</strong>{" "}
          {verdict.stamp.unverified.join(", ")}
        </div>
      ) : null}

      {verdict.reasons.length > 0 ? (
        <>
          <h3>Reasons</h3>
          <ul className="reasons">
            {verdict.reasons.map((r, i) => (
              <li key={`${r.code}-${i}`}>
                <strong>{r.message}</strong>
                <div className="muted small">
                  <code>{r.code}</code> · {r.reference}
                </div>
                {r.remedy ? (
                  <div>
                    <em>Remedy:</em> {r.remedy}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {verdict.warnings && verdict.warnings.length > 0 ? (
        <>
          <h3>Warnings</h3>
          <ul className="warnings">
            {verdict.warnings.map((w, i) => (
              <li key={`${w.code}-${i}`}>
                {w.message} <code>{w.code}</code>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h3>Where the data came from</h3>
      <dl className="assembled">
        <div>
          <dt>Jurisdiction</dt>
          <dd>{humanise(assembledFrom.jurisdiction)}</dd>
        </div>
        <div>
          <dt>Previous heating fuel</dt>
          <dd>{humanise(assembledFrom.previousHeatingFuel)}</dd>
        </div>
        <div>
          <dt>EPC</dt>
          <dd>{humanise(assembledFrom.epc)}</dd>
        </div>
        <div>
          <dt>MCS certification</dt>
          <dd>{humanise(assembledFrom.mcsCertified)}</dd>
        </div>
      </dl>
    </section>
  );
}
